import * as React from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { invalidateShipmentLists } from '#/entities/shipment'
import type { ShipmentReasonResponse } from '#/shared/api/generated/shipment/models'
import { ShipmentStatus } from '#/shared/api/generated/shipment/models'
import {
  useListReasonsApiV1ShipmentReasonsGet,
  useUpdateShipmentApiV1ShipmentsShipmentIdPatch,
} from '#/shared/api/generated/shipment/theCargoShipment'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'
import { cn } from '#/shared/lib/utils'
import { Button } from '#/shared/ui/button'
import {
  ArchiveIcon,
  CheckboxCheckedIcon,
  ChevronDownIcon,
} from '#/shared/ui/icons'
import { Popover } from '#/shared/ui/popover'
import { Skeleton } from '#/shared/ui/skeleton'
import { toast } from '#/shared/ui/sonner'

const REASONS_PAGE_SIZE = 100

export interface ArchiveReasonDropdownProps {
  label: string
  stage?: string
  /**
   * When provided, submitting the popover PATCHes the shipment with
   * `status: 'archived'` + the selected `reason_ids` and fires
   * `onArchived` on success. Without it the dropdown just emits
   * `onSelect(reasons[])` and the caller handles the rest.
   */
  shipmentId?: string
  onArchived?: () => void
  onSelect?: (reasons: ShipmentReasonResponse[]) => void
}

function useArchiveReasons(stage: string | undefined, enabled: boolean) {
  const query = useListReasonsApiV1ShipmentReasonsGet(
    {
      stage: stage ?? null,
      active: true,
      size: REASONS_PAGE_SIZE,
    },
    { query: { enabled } },
  )
  return {
    reasons: query.data?.data.items ?? [],
    isLoading: query.isLoading && enabled,
  }
}

function ReasonsEmpty() {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col items-center gap-2 px-2.5 py-6 text-center">
      <div className="flex size-10 items-center justify-center rounded-full bg-gray-100 text-gray-500">
        <ArchiveIcon className="size-5" />
      </div>
      <p className="text-sm font-medium text-gray-900">
        {t('quoteDetails.archiveReasons.empty')}
      </p>
      <p className="text-xs text-gray-500">
        {t('quoteDetails.archiveReasons.emptyHint')}
      </p>
    </div>
  )
}

function ReasonsSkeleton() {
  return (
    <>
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex h-8 items-center px-2.5">
          <Skeleton className="h-4 w-full" />
        </div>
      ))}
    </>
  )
}

function useArchiveSubmit(onArchived: (() => void) | undefined) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  return useUpdateShipmentApiV1ShipmentsShipmentIdPatch({
    mutation: {
      onSuccess: (_res, variables) => {
        invalidateShipmentLists(queryClient)
        queryClient.invalidateQueries({
          queryKey: [`/api/v1/shipments/${variables.shipmentId}`],
        })
        toast.success(t('quoteDetails.archiveReasons.archiveSuccess'))
        onArchived?.()
      },
      onError: (error) => {
        toast.error(
          getApiErrorDetail(error) ??
            t('quoteDetails.archiveReasons.archiveError'),
        )
      },
    },
  })
}

export function ArchiveReasonsPanel({
  open,
  stage,
  shipmentId,
  onArchived,
  onSelect,
  onClose,
}: {
  open: boolean
  stage: string | undefined
  shipmentId: string | undefined
  onArchived: (() => void) | undefined
  onSelect?: (reasons: ShipmentReasonResponse[]) => void
  onClose: () => void
}) {
  const { t } = useTranslation()
  const firstItemRef = React.useRef<HTMLButtonElement | null>(null)
  const { reasons, isLoading } = useArchiveReasons(stage, open)
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set())
  const submit = useArchiveSubmit(() => {
    onClose()
    onArchived?.()
  })

  React.useEffect(() => {
    if (!open) setSelectedIds(new Set())
  }, [open])

  React.useEffect(() => {
    if (open) firstItemRef.current?.focus()
  }, [open, reasons.length])

  const isEmpty = !isLoading && reasons.length === 0
  const hasReasons = !isLoading && reasons.length > 0
  const selectedReasons = reasons.filter((r) => selectedIds.has(r.id))

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleSubmit = () => {
    if (selectedReasons.length === 0) return
    onSelect?.(selectedReasons)
    if (!shipmentId) {
      onClose()
      return
    }
    submit.mutate({
      shipmentId,
      data: {
        status: ShipmentStatus.archived,
        reason_ids: selectedReasons.map((r) => r.id),
      },
    })
  }

  return (
    <div className="flex w-56 flex-col gap-2.5 rounded-md border border-gray-200 bg-white p-1 shadow-popover">
      <div className="flex flex-col gap-1.25">
        {hasReasons && (
          <div className="flex h-6 items-center border-b border-gray-200 px-0.5 text-xs text-gray-500">
            {t('quoteDetails.archiveReasons.title')}
          </div>
        )}
        <div className="flex flex-col gap-0.5">
          {isLoading ? (
            <ReasonsSkeleton />
          ) : isEmpty ? (
            <ReasonsEmpty />
          ) : (
            reasons.map((reason, i) => {
              const isSelected = selectedIds.has(reason.id)
              return (
                <button
                  key={reason.id}
                  ref={i === 0 ? firstItemRef : undefined}
                  type="button"
                  onClick={() => toggle(reason.id)}
                  className={cn(
                    'flex h-8 w-full cursor-pointer items-center justify-between gap-2 rounded-sm px-2.5 text-left text-sm text-gray-900 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none',
                    isSelected && 'bg-gray-100',
                  )}
                >
                  <span className="truncate">{reason.text}</span>
                  {isSelected && (
                    <CheckboxCheckedIcon className="size-4 shrink-0 text-primary" />
                  )}
                </button>
              )
            })
          )}
        </div>
      </div>
      {hasReasons && (
        <Button
          type="button"
          variant="soft-destructive"
          size="xs"
          className="w-full"
          disabled={selectedReasons.length === 0 || submit.isPending}
          onClick={handleSubmit}
        >
          {submit.isPending ? t('table.saving') : t('detailToolbar.archive')}
        </Button>
      )}
    </div>
  )
}

export function ArchiveReasonDropdown({
  label,
  stage,
  shipmentId,
  onArchived,
  onSelect,
}: ArchiveReasonDropdownProps) {
  return (
    <div className="w-fit">
      <Popover
        align="right"
        trigger={(open) => (
          <Button
            variant="outline"
            size="xs"
            className={cn(
              'gap-2.5 px-2.5 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:focus-ring',
              open && 'focus-ring',
            )}
          >
            {label}
            <ChevronDownIcon
              className={cn(
                'size-3 transition-transform duration-200',
                open && 'rotate-180',
              )}
            />
          </Button>
        )}
      >
        {(close, open) => (
          <ArchiveReasonsPanel
            open={open}
            stage={stage}
            shipmentId={shipmentId}
            onArchived={onArchived}
            onSelect={onSelect}
            onClose={close}
          />
        )}
      </Popover>
    </div>
  )
}
